import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { ActionIconDef, GridConfig, GridRow } from '@inavista/components/grid';

// Cycle Counts popup → Cost Analysis on @inavista/components. Same columns, labels, order, widths, formats, role-hidden
// money columns, red negatives, yellow adjusted rows, totals row (Total, Ext Cost, both variances, Cost VAR with the
// cost difference %), Item Link command and toolbar (Columns, Upload File, VAR (RIMS) / VAR (LN) / Adjusted, Search,
// Excel export, Clear filters) as the Syncfusion grid it replaces. Rows are the popup's own itemList
// (costAnalysisList, LocalRowsGridApi). Adj Count and Comments are edited inline while the count is Counted.

export const COST_ANALYSIS_URL = 'page://cycle-count-cost-analysis';
export const COST_ANALYSIS_PERSISTENCE_KEY = 'rims.service.cycle-count-popup-cost-analysis';
export const UPLOAD_FILE_ACTION = 'upload-file';
export const RIMS_VAR_ACTION = 'rimsVar';
export const LN_VAR_ACTION = 'lnVar';
export const ADJUSTED_ACTION = 'adjusted';
export const ITEM_LINK_ACTION = 'Item Link';
/** The old row colour of an item whose count was adjusted (screen and Excel). */
export const ADJUSTED_ROW_COLOR = '#FFFF00';

// fas fa-upload, fa fa-link
const UPLOAD_ICON = '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />';
const LINK_ICON = '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />';

export const COST_ANALYSIS_COLUMNS: [string, string][] = [
  ['itemCode', 'nvarchar'],
  ['itemDescription', 'nvarchar'],
  ['cost', 'decimal'],
  ['rimsQtyOnhand', 'decimal'],
  ['lnQtyOnhand', 'decimal'],
  ['extCost', 'decimal'],
  ['actualQtyOnhand', 'decimal'],
  ['actualQtyCorrected', 'decimal'],
  ['rimsVarianceQty', 'decimal'],
  ['lnVarianceQty', 'decimal'],
  ['costVariance', 'decimal'],
  ['comments', 'nvarchar'],
];

const ORDER = COST_ANALYSIS_COLUMNS.map(([c]) => c);
const MONEY: Intl.NumberFormatOptions = { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 };
// Syncfusion's 'n': grouped, up to 3 decimals.
const NUMBER: Intl.NumberFormatOptions = { maximumFractionDigits: 3 };
const twoDecimals = (v: unknown) => Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** An item whose count was adjusted (the old queryCellInfo / excelQueryCellInfo rule). */
export function isAdjustedItem(row: GridRow): boolean {
  return row?.['actualQtyOnhand'] != null && row?.['actualQtyCorrected'] != null && row['actualQtyOnhand'] != row['actualQtyCorrected'];
}

export interface CostAnalysisGridContext {
  /** Columns the role can't see (Unit Cost, Ext Cost (LN), Cost VAR (LN-ADJ)): hidden and out of the column chooser. */
  roleHidden: string[];
  canEdit: () => boolean;
  uploadDisabledReason: () => string | null;
  /** The cost difference % shown after the Cost VAR total, or null. */
  costDifference: () => number | null;
  linkColor: (row: GridRow) => string;
}

export function buildCostAnalysisActionIcons(ctx: CostAnalysisGridContext): ActionIconDef[] {
  // The old command column: one Item Link, coloured by the row's linkColorCode.
  return [{ key: ITEM_LINK_ACTION, label: 'Item Link', icon: LINK_ICON, colorFn: row => ctx.linkColor(row) }];
}

export function buildCostAnalysisGridConfig(ctx: CostAnalysisGridContext): GridConfig {
  return {
    apiBaseUrl: COST_ANALYSIS_URL,
    clientSide: true,
    keyColumn: 'itemCode',
    rowHeight: 30,
    defaultColumnOrder: ORDER,
    hiddenByDefaultColumns: ctx.roleHidden,
    columnLabels: {
      itemCode: 'Items', itemDescription: 'Description', cost: 'Unit Cost', rimsQtyOnhand: 'On hand (RIMS)',
      lnQtyOnhand: 'On hand (LN)', extCost: 'Ext Cost (LN)', actualQtyOnhand: 'Cycle Count', actualQtyCorrected: 'Adj Count',
      rimsVarianceQty: 'VAR (ADJ-RIMS)', lnVarianceQty: 'VAR (ADJ-LN)', costVariance: 'Cost VAR (LN-ADJ)', comments: 'Comments',
    },
    // Every column left, as the old grid.
    columnAlign: Object.fromEntries(ORDER.map(c => [c, { horizontal: 'left' as const, header: { horizontal: 'left' as const } }])),
    // Every old column was 200 wide.
    columnWidths: Object.fromEntries(ORDER.map(c => [c, 200])),
    stretchColumns: false,
    columnNumberFormats: {
      cost: MONEY, extCost: MONEY, rimsQtyOnhand: NUMBER, lnQtyOnhand: NUMBER, actualQtyOnhand: NUMBER,
      actualQtyCorrected: NUMBER, rimsVarianceQty: NUMBER, lnVarianceQty: NUMBER,
    },
    // Cost VAR showed "$" and two decimals, and nothing for 0; its total adds the cost difference %.
    columnFormatters: {
      costVariance: (value, row) => {
        const isTotal = Object.keys(row ?? {}).length === 1;
        if (isTotal) {
          const pct = ctx.costDifference();
          return `$${twoDecimals(value ?? 0)}${pct ? ` (${pct}%)` : ''}`;
        }
        return value ? `$${twoDecimals(value)}` : '';
      },
    },
    // Negative variances in red, as the old templates.
    conditionalFormats: ['rimsVarianceQty', 'lnVarianceQty', 'costVariance'].map(column => ({
      column, when: 'lessThan', value: 0, style: { color: 'red' },
    })),
    rowToneFn: row => isAdjustedItem(row) ? { bg: ADJUSTED_ROW_COLOR } : null,
    hyperlinkColumns: ['itemCode', 'itemDescription'],
    // The old footer: "Total" under Items, then the sums.
    totals: { extCost: 'sum', rimsVarianceQty: 'sum', lnVarianceQty: 'sum', costVariance: 'sum' },
    totalsLabel: 'Total',
    // The old Actions column (frozen right) was 170 wide.
    actionsColumnMinWidth: 170,
    // Rows open only for a user who may change them, as the old editSettings.allowEditing (the page rebuilds
    // the config when that changes): a row whose every cell is locked would still open, with nothing to edit.
    editMode: ctx.canEdit() ? 'inline' : 'none',
    editSaveMode: 'row',
    // A double click opens the row, as the Syncfusion grid.
    editTrigger: 'doubleClick',
    editableColumns: ['actualQtyCorrected', 'comments'],
    // Adj Count and Comments are edited while the cycle count is Counted.
    readOnlyCellFn: () => !ctx.canEdit(),
    deleteMode: 'host',
    rowDeletable: () => false,
    toolbarActions: [
      {
        key: UPLOAD_FILE_ACTION, label: 'Upload File', kind: 'button', icon: UPLOAD_ICON,
        disabled: () => !!ctx.uploadDisabledReason(), disabledReason: () => ctx.uploadDisabledReason() ?? '',
      },
      { key: RIMS_VAR_ACTION, label: 'VAR (RIMS)', kind: 'toggle', icon: '', showLabel: true, position: 'end' },
      { key: LN_VAR_ACTION, label: 'VAR (LN)', kind: 'toggle', icon: '', showLabel: true, position: 'end' },
      { key: ADJUSTED_ACTION, label: 'Adjusted', kind: 'toggle', icon: '', showLabel: true, position: 'end' },
    ],
    // The old toolbar: Columns, Upload File, Refresh, the three checkboxes, Search, Excel export (every user), Clear
    // filters. Refresh is not kept: it only redrew the same rows.
    enableColumnsPicker: true,
    enableExport: true,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    multiSort: true,
    emptyStateMessage: 'No records to display',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: COST_ANALYSIS_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
